import type {PropertyValue, ValueType} from '@github-ui/custom-properties-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Checkbox, FormControl} from '@primer/react'

import {CustomPropertyInput} from '../CustomPropertyInput'
import {PropertyValueField} from './PropertyValueField'
import styles from './RequiredDefinitionEditor.module.css'

interface Props {
  checkboxLabel?: string
  checkboxCaption?: string
  defaultValueCaption?: string
  isRequired: boolean
  valueType: ValueType
  defaultValue: PropertyValue
  defaultValueError?: string
  allowableDefaultValues: string[]
  propertyName: string
  onRequiredChange: (required: boolean) => void
  onDefaultValueChange: (value: PropertyValue) => void
  setDefaultValueInputRef?: (ref: HTMLElement | null) => void
  requiredFormExtension?: React.ReactNode
  className?: string
}

export function RequiredDefinitionEditor({
  checkboxLabel = 'Require this property for all targets',
  checkboxCaption = "Targets that don't have an explicit value for this property will inherit the default value.",
  defaultValueCaption = 'This is the default value that will be set for this property.',
  isRequired,
  valueType,
  defaultValue,
  defaultValueError,
  allowableDefaultValues: providedDefaultValues,
  propertyName,
  onRequiredChange,
  onDefaultValueChange,
  setDefaultValueInputRef,
  requiredFormExtension,
  className,
}: Props) {
  const isText = valueType === 'string'
  const isTrueFalse = valueType === 'true_false'
  const isUrl = valueType === 'url'

  const defaultValueInvalid = !!defaultValueError
  const defaultValueAriaAriaProp = {
    'aria-invalid': defaultValueInvalid,
    'aria-labelledby': 'property-default-value-label',
    'aria-describedby': `${
      defaultValueInvalid ? 'property-default-value-validation' : ''
    } property-default-value-caption`,
  }

  const allowableDefaultValues = (isTrueFalse ? ['true', 'false'] : providedDefaultValues) || null

  const reusablePropertyValueFieldEnabled = isFeatureEnabled('custom_properties_core_reusable_property_value_field')

  return (
    <div className={className}>
      <FormControl>
        <Checkbox checked={isRequired} onChange={() => onRequiredChange(!isRequired)} />
        <FormControl.Label>{checkboxLabel}</FormControl.Label>
        <FormControl.Caption>{checkboxCaption}</FormControl.Caption>
      </FormControl>
      <div className={styles.optionsInputGroup}>
        {isRequired && (
          <>
            {reusablePropertyValueFieldEnabled ? (
              <PropertyValueField
                required
                idPrefix="property-default-value"
                className={styles.defaultValueFormControl}
                label="Default value"
                caption={defaultValueCaption}
                propertyName={propertyName}
                setInputRef={setDefaultValueInputRef}
                value={defaultValue}
                onChange={onDefaultValueChange}
                valueType={valueType}
                allowedValues={providedDefaultValues}
                error={defaultValueError}
              />
            ) : (
              <FormControl className={styles.defaultValueFormControl}>
                <FormControl.Label id="property-default-value-label">Default value *</FormControl.Label>

                <div className={isText || isUrl ? 'width-full' : styles.selectPanelContainer}>
                  <CustomPropertyInput
                    propertyName={propertyName}
                    setRef={setDefaultValueInputRef}
                    propertyValue={defaultValue}
                    onChange={onDefaultValueChange}
                    valueType={valueType}
                    allowedValues={allowableDefaultValues}
                    anchorProps={defaultValueAriaAriaProp}
                    inputProps={defaultValueAriaAriaProp}
                    defaultValue={null}
                    mixed={false}
                  />
                </div>

                {defaultValueError && (
                  <FormControl.Validation
                    id="property-default-value-validation"
                    variant="error"
                    className={styles.validationFeedback}
                  >
                    {defaultValueError}
                  </FormControl.Validation>
                )}
                <FormControl.Caption id="property-default-value-caption">{defaultValueCaption}</FormControl.Caption>
              </FormControl>
            )}
            {requiredFormExtension}
          </>
        )}
      </div>
    </div>
  )
}
